// ============================================
// fingerprint.js - Device Fingerprinting Service
// ============================================
// Generates consistent device fingerprint for anonymous user tracking
// Combines multiple browser signals for reliable identification

const FP_STORAGE_KEY = 'ghostink_device_fp';

// ============================================
// FINGERPRINT COMPONENTS
// ============================================

/**
 * Canvas fingerprint - unique based on GPU/driver rendering
 */
async function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    canvas.width = 200;
    canvas.height = 50;

    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('GhostInk fp', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('GhostInk fp', 4, 17);

    const dataUrl = canvas.toDataURL();
    return await hashString(dataUrl);
  } catch {
    return '';
  }
}

/**
 * WebGL fingerprint - unique based on GPU info
 */
function getWebGLFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '';

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return '';

    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

    return `${vendor}~${renderer}`;
  } catch {
    return '';
  }
}

/**
 * Audio fingerprint - unique based on audio processing
 */
async function getAudioFingerprint() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return '';

    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const analyser = context.createAnalyser();
    const gain = context.createGain();

    gain.gain.value = 0; // Mute
    oscillator.type = 'triangle';
    oscillator.connect(analyser);
    analyser.connect(gain);
    gain.connect(context.destination);

    oscillator.start(0);

    const data = new Float32Array(analyser.frequencyBinCount);
    analyser.getFloatFrequencyData(data);

    oscillator.stop();
    await context.close();

    const sum = data.slice(0, 100).reduce((a, b) => a + b, 0);
    return sum.toString();
  } catch {
    return '';
  }
}

/**
 * Simple hash function using Web Crypto API
 */
async function hashString(str) {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const msgBuffer = new TextEncoder().encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  // Fallback simple hash
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function getScreenInfo() {
  return `${screen.width}x${screen.height}x${screen.colorDepth}`;
}

function getTimezoneOffset() {
  return new Date().getTimezoneOffset();
}

function getPlatform() {
  return navigator.platform || '';
}

function getLanguage() {
  return navigator.language || '';
}

// ============================================
// PUBLIC API
// ============================================

/**
 * Generate full device fingerprint data
 * @returns {Promise<Object>} Fingerprint data object
 */
export async function generateFingerprint() {
  const [canvasHash, audioHash] = await Promise.all([
    getCanvasFingerprint(),
    getAudioFingerprint(),
  ]);

  return {
    user_agent: navigator.userAgent,
    screen_resolution: getScreenInfo(),
    timezone_offset: getTimezoneOffset(),
    language: getLanguage(),
    platform: getPlatform(),
    canvas_hash: canvasHash,
    webgl_hash: getWebGLFingerprint(),
    audio_hash: audioHash,
  };
}

/**
 * Generate fingerprint hash (for X-Fingerprint-Hash header)
 * @returns {Promise<string>} SHA-256 hash of fingerprint
 */
export async function getFingerprintHash() {
  const fp = await generateFingerprint();
  const combined = [
    fp.user_agent,
    fp.screen_resolution,
    fp.timezone_offset.toString(),
    fp.language,
    fp.platform,
    fp.canvas_hash,
    fp.webgl_hash,
    fp.audio_hash,
  ].join('|');

  return hashString(combined);
}

/**
 * Get or generate fingerprint (cached in localStorage)
 * @returns {Promise<{fingerprint: Object, hash: string}>}
 */
export async function getStoredFingerprint() {
  // Check cache first
  const cached = localStorage.getItem(FP_STORAGE_KEY);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      // Validate it has expected structure
      if (parsed.fingerprint && parsed.hash) {
        return parsed;
      }
    } catch {
      // Invalid cache, will regenerate
    }
  }

  // Generate new fingerprint
  const fingerprint = await generateFingerprint();
  const hash = await getFingerprintHash();

  const data = { fingerprint, hash };
  localStorage.setItem(FP_STORAGE_KEY, JSON.stringify(data));

  return data;
}

/**
 * Clear stored fingerprint (for testing/debugging)
 */
export function clearStoredFingerprint() {
  localStorage.removeItem(FP_STORAGE_KEY);
}

/**
 * Get just the hash (convenience method)
 * @returns {Promise<string>}
 */
export async function getDeviceHash() {
  const { hash } = await getStoredFingerprint();
  return hash;
}
